import { type CartItem } from '@alto/orders_api/types/v1/cart_item';
import { useEffect, useMemo, useRef } from 'react';
import { EXPENSIVE, HAS_CONTROLS, MAIL_ONLY_ZONE, NO_SAFE_PLACE } from '~shared/features/checkout/constants';
import { type AddressInfoKey } from '~shared/features/checkout/types';
import { getDispensingFeeAnalyticsParams } from '~shared/features/pricing/programDispensingFee';
import { type OrderPricing } from '~shared/features/pricing/types';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useCheckoutAddressInfoKeys } from './useCheckoutAddressInfoKeys';

const WAIVABLE = 'waivable';
const MUST_BE_HOME = 'must be home';

type HomeToSignRequirements = typeof WAIVABLE | typeof MUST_BE_HOME;

export const getHomeToSignRequirements = (
  keys: Partial<Record<AddressInfoKey, boolean>>,
): HomeToSignRequirements | null => {
  if ((keys[MAIL_ONLY_ZONE] && (keys[HAS_CONTROLS] || keys[EXPENSIVE])) || keys[HAS_CONTROLS]) {
    return MUST_BE_HOME;
  }

  if (keys[EXPENSIVE] && keys[NO_SAFE_PLACE]) {
    return WAIVABLE;
  }

  if (keys[NO_SAFE_PLACE]) {
    return MUST_BE_HOME;
  }

  return null;
};

type Props = {
  shipmentID: string | number | null | undefined;
  items: CartItem[];
  isEditingOrder: boolean;
  isSplitShipment: boolean;
  mailRequestRequired: boolean;
  orderPricing?: OrderPricing;
  isOrderPricingLoading: boolean;
};

export const useCheckoutViewed = ({
  shipmentID,
  items,
  isEditingOrder,
  isSplitShipment,
  mailRequestRequired,
  orderPricing,
  isOrderPricingLoading,
}: Props) => {
  const { keys } = useCheckoutAddressInfoKeys();
  const homeToSignRequirements = getHomeToSignRequirements(keys);
  const { trackEvent } = useAnalytics();
  const hasOrderPricing = !!orderPricing;
  const { has_program_dispensing_fee, total_program_dispensing_fee } = useMemo(
    () => getDispensingFeeAnalyticsParams(orderPricing?.line_items),
    [orderPricing?.line_items],
  );
  const runOnce = useRef(false);
  const prescriptionCount = items.reduce((count, item) => {
    count += item.resource_type === 'Prescription' ? 1 : 0;
    return count;
  }, 0);

  useEffect(() => {
    if (isOrderPricingLoading || !hasOrderPricing) return; // ensure accurate analytics for dispensing fees
    if (runOnce.current) return;
    runOnce.current = true;
    trackEvent({
      event: EVENTS.CHECKOUT_VIEWED,
      params: {
        origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
        shipmentId: isEditingOrder ? shipmentID : null,
        'home to sign requirements': homeToSignRequirements,
        isSplitShipment,
        prescriptionCount,
        mailRequestRequired,
        has_program_dispensing_fee,
        total_program_dispensing_fee,
      },
    });
  }, [
    homeToSignRequirements,
    isEditingOrder,
    isSplitShipment,
    prescriptionCount,
    shipmentID,
    trackEvent,
    mailRequestRequired,
    has_program_dispensing_fee,
    total_program_dispensing_fee,
    isOrderPricingLoading,
    hasOrderPricing,
  ]);
};
